// DATA-SERIES TOKENS — the app's own colours for telling one series from
// another, when no design has said anything (AG-2106).
//
// Part of the token layer, beside `tokens.css`, and deliberately in TypeScript
// rather than in it. A chart draws inside a sandboxed frame with an opaque
// origin and no app stylesheet, so `var(--chart-series-1)` resolves to nothing
// there and the mark renders with no fill at all. These have to be real values
// at the point of use, and being real values they are stated ONCE.
//
// WHAT THIS REPLACES.
//
// `chartProps.FALLBACK_PALETTE` was an eight-hue Tableau-style ramp defined in
// the renderer — the floor under every mark, so a tile whose design supplied no
// palette was painted in a look invented by one frontend module. That is a
// second design system sitting under the dashboard theme, and it disagreed with
// the answer the backend already gives: `design_generator._heuristic` renders an
// undesigned board in the app's own font token and the Okabe-Ito ramp,
// explicitly so an undesigned board does NOT look designed.
//
// One question, one answer. This is that ramp, on this side of the wire.

/**
 * Eight hues that stay distinguishable under protanopia, deuteranopia and
 * tritanopia (Okabe & Ito, 2008).
 *
 * Chosen for SEPARABILITY, not for feel — which is what a floor is for, and
 * which happens to make it the least brand-like set available. It is not a house
 * style and must never become one: a board with a design uses that design's
 * palette, and this is only ever reached when there is none.
 *
 * Kept in step with `design_generator._heuristic`, which states the same set for
 * a board whose design call failed. Two spellings of one decision is how they
 * drift, so the comment there names this file and this comment names that
 * function.
 */
export const NEUTRAL_SERIES = [
  '#0072B2', // blue
  '#E69F00', // orange
  '#009E73', // bluish green
  '#CC79A7', // reddish purple
  '#56B4E9', // sky blue
  '#D55E00', // vermillion
  '#F0E442', // yellow
  '#999999', // grey
] as const;

/**
 * The three things a mark can SAY about a value, as colour, when no board has
 * said it differently.
 *
 * A status colour is semantic — good, watch, bad — and every layer of this
 * product already knows that: `design_system.default_color_roles()` resolves the
 * triad per mode and publishes it as the theme's `color_roles`, the KPI seed
 * reads it as `statusColors`, and a design or a reference palette may override
 * any role. This is the same vocabulary on this side of the wire, for the marks
 * the overlay builder adds — a peak, a low, an anomaly — which were three raw
 * hexes in `chartProps` and therefore a fourth opinion about what green means
 * (AG-2106).
 *
 * The values match `design_system._STATUS_ROLES_LIGHT`, deliberately: an overlay
 * marker and the KPI beside it must agree about "good", and two spellings of one
 * decision is how they drift.
 */
export const NEUTRAL_STATUS = {
  positive: '#16a34a',
  warning: '#d97706',
  negative: '#dc2626',
  neutral: '#64748b',
} as const;

export type StatusRole = keyof typeof NEUTRAL_STATUS;
